import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { File, Upload, Search, Download, Eye, Trash2, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import LaudoModal from '@/components/pericias/LaudoModal';

export default function DocumentosSystemPage() {
  const { toast } = useToast();
  const [laudos, setLaudos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLaudo, setSelectedLaudo] = useState(null);

  useEffect(() => {
    loadDocumentos();
  }, []);

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      const response = await api.laudos.list();
      if (response.data) {
        setLaudos(response.data);
      } else if (response.error) {
        toast({
          title: "Erro ao buscar documentos",
          description: response.error,
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Tem certeza que deseja excluir este documento?")) return;

    try {
      const result = await api.laudos.delete(id);
      if (!result.error) {
        toast({ title: "Sucesso", description: "Documento excluído." });
        loadDocumentos();
      } else {
        toast({ title: "Erro", description: result.error, variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Erro", description: "Falha na requisição de exclusão.", variant: "destructive" });
    }
  };

  const handleAction = () => {
    toast({
      title: "Ação Indisponível",
      description: "🚧 Esta função ainda não foi conectada.",
    });
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <Helmet>
        <title>Documentos - PROVATIO</title>
      </Helmet>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg text-[#193D6D]">
            <File size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#193D6D]">Repositório de Documentos</h1>
            <p className="text-slate-500">Gerencie todos os arquivos e anexos do sistema.</p>
          </div>
        </div>
        <Button onClick={() => { setSelectedLaudo(null); setModalOpen(true); }} className="bg-[#193D6D] hover:bg-blue-800 text-white shadow-md">
          <Upload className="w-4 h-4 mr-2" /> Upload de Arquivo
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b bg-slate-50 flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input placeholder="Buscar por nome do documento..." className="pl-9 bg-white text-slate-900" />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome do Arquivo</TableHead>
              <TableHead>Processo Relacionado</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Tamanho</TableHead>
              <TableHead>Data Criação</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Carregando documentos...
                </TableCell>
              </TableRow>
            ) : laudos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhum documento encontrado.
                </TableCell>
              </TableRow>
            ) : (
              laudos.map((doc) => {
                const docName = doc.arquivo_url ? doc.arquivo_url.split('/').pop() : `Laudo_v${doc.versao}.pdf`;
                const dateFmt = doc.created_at ? new Date(doc.created_at).toLocaleDateString('pt-BR') : '-';

                return (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium text-blue-600 flex items-center gap-2">
                      <File className="w-4 h-4 text-slate-400" />
                      {doc.titulo || docName}
                    </TableCell>
                    <TableCell>{doc.processo_numero || `Proc ID: ${doc.processo_id}`}</TableCell>
                    <TableCell>{doc.tipo || 'PDF'}</TableCell>
                    <TableCell>{doc.status === 'rascunho' ? 'Rascunho' : 'Finalizado'}</TableCell>
                    <TableCell>{dateFmt}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedLaudo(doc); setModalOpen(true); }} className="h-8 w-8 text-blue-500 hover:bg-blue-50">
                          <Edit3 size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(doc.id)} className="h-8 w-8 text-red-500 hover:bg-red-50 text-muted-foreground hover:text-red-600">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {modalOpen && (
        <LaudoModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          laudo={selectedLaudo}
          onSuccess={loadDocumentos}
        />
      )}
    </div>
  );
}